/**
 * TopPublishersWidgetPage — ranked table of top publishers (affiliates).
 *
 * Ported from pre-nuke legacy `apps/dash-legacy/src/routes/_authed/publishers/$id.group-by.tsx`
 * (MVP-32 publisher performance group-by). Modernized: plain Tailwind only, TanStack Query,
 * raw fetch confined to ./api, emerald/sky gradient headline, zinc borders, rounded-md/lg.
 *
 * Features:
 *  - Date-range selector (7d / 30d / 90d / YTD).
 *  - Top-N selector (5 / 10 / 25).
 *  - Ranked table: rank, affiliate, clicks, conversions, CR, payout.
 *  - Inline SVG sparkline per row (emerald).
 *  - "View all publishers" link at bottom.
 */
import { useMemo, useState } from "react";
import { useTopPublishersWidgetList, type PublisherRow } from "./hooks";

type Period = "7d" | "30d" | "90d" | "ytd";
type TopN = 5 | 10 | 25;

const CARD = "rounded-lg border border-zinc-200 bg-white p-5 shadow-sm";
const TH = "px-3 py-2 text-left text-xs font-medium uppercase tracking-wider text-zinc-500";
const TD = "px-3 py-2 text-sm text-zinc-900";
const PILL_BASE =
	"inline-flex items-center rounded-md border px-2.5 py-1 text-xs font-medium transition-colors";
const PILL_ON = "border-emerald-400 bg-emerald-50 text-emerald-700";
const PILL_OFF = "border-zinc-200 bg-white text-zinc-600 hover:bg-zinc-50";

const PERIOD_OPTIONS: { value: Period; label: string }[] = [
	{ value: "7d", label: "7d" },
	{ value: "30d", label: "30d" },
	{ value: "90d", label: "90d" },
	{ value: "ytd", label: "YTD" },
];

const TOPN_OPTIONS: TopN[] = [5, 10, 25];

function formatNum(n: number | undefined | null): string {
	if (typeof n !== "number" || Number.isNaN(n)) return "—";
	return n.toLocaleString();
}

function formatPct(n: number | undefined | null): string {
	if (typeof n !== "number" || Number.isNaN(n)) return "—";
	return `${(n * 100).toFixed(2)}%`;
}

function formatMoney(n: number | undefined | null): string {
	if (typeof n !== "number" || Number.isNaN(n)) return "—";
	return `$${n.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

function Sparkline({ points }: { points: number[] }) {
	if (!points || points.length === 0) {
		return (
			<svg
				viewBox="0 0 80 24"
				className="h-6 w-20"
				aria-label="sparkline (no data)"
				data-testid="top-publishers-widget-sparkline-empty"
			>
				<line x1="0" y1="12" x2="80" y2="12" stroke="#d4d4d8" strokeWidth="1" />
			</svg>
		);
	}
	const max = Math.max(...points);
	const min = Math.min(...points);
	const range = max - min || 1;
	const w = 80;
	const h = 24;
	const step = points.length > 1 ? w / (points.length - 1) : 0;
	const coords = points
		.map((v, i) => {
			const x = i * step;
			const y = h - ((v - min) / range) * h;
			return `${x.toFixed(2)},${y.toFixed(2)}`;
		})
		.join(" ");
	return (
		<svg
			viewBox={`0 0 ${w} ${h}`}
			className="h-6 w-20"
			aria-label="sparkline"
			data-testid="top-publishers-widget-sparkline"
		>
			<polyline
				points={coords}
				fill="none"
				stroke="#059669"
				strokeWidth="1.5"
				strokeLinecap="round"
				strokeLinejoin="round"
			/>
		</svg>
	);
}

export function TopPublishersWidgetPage() {
	const [period, setPeriod] = useState<Period>("7d");
	const [topN, setTopN] = useState<TopN>(10);

	const listQuery = useTopPublishersWidgetList({ period, limit: topN });

	const items: PublisherRow[] = useMemo(
		() => listQuery.data?.items ?? [],
		[listQuery.data],
	);

	return (
		<main
			data-testid="top-publishers-widget-page"
			className="mx-auto flex w-full max-w-6xl flex-col gap-8 px-6 py-10"
		>
			<header className="flex flex-col gap-2">
				<p className="text-xs font-semibold uppercase tracking-wider text-zinc-500">
					Report
				</p>
				<h1 className="bg-gradient-to-r from-emerald-600 to-sky-600 bg-clip-text text-3xl font-semibold text-transparent">
					Top Publishers
				</h1>
				<p className="text-sm text-zinc-500">
					Ranked publishers by clicks, conversions, conversion rate, and payout.
				</p>
			</header>

			<section className={CARD} aria-label="Top publishers controls">
				<div className="flex flex-wrap items-center gap-4">
					<div
						className="flex items-center gap-2"
						data-testid="top-publishers-widget-period"
						role="group"
						aria-label="Date range"
					>
						<span className="text-xs font-medium uppercase tracking-wider text-zinc-500">
							Range
						</span>
						{PERIOD_OPTIONS.map((opt) => (
							<button
								type="button"
								key={opt.value}
								onClick={() => setPeriod(opt.value)}
								aria-pressed={period === opt.value}
								data-testid={`top-publishers-widget-period-${opt.value}`}
								className={`${PILL_BASE} ${period === opt.value ? PILL_ON : PILL_OFF}`}
							>
								{opt.label}
							</button>
						))}
					</div>

					<div
						className="flex items-center gap-2"
						data-testid="top-publishers-widget-topn"
						role="group"
						aria-label="Top N"
					>
						<span className="text-xs font-medium uppercase tracking-wider text-zinc-500">
							Top
						</span>
						{TOPN_OPTIONS.map((n) => (
							<button
								type="button"
								key={n}
								onClick={() => setTopN(n)}
								aria-pressed={topN === n}
								data-testid={`top-publishers-widget-topn-${n}`}
								className={`${PILL_BASE} ${topN === n ? PILL_ON : PILL_OFF}`}
							>
								{n}
							</button>
						))}
					</div>
				</div>
			</section>

			<section className={CARD} aria-label="Top publishers report">
				{listQuery.isLoading ? (
					<p
						className="text-sm text-zinc-500"
						data-testid="top-publishers-widget-loading"
					>
						Loading.
					</p>
				) : listQuery.error ? (
					<p className="text-sm text-red-600" role="alert">
						{listQuery.error.message}
					</p>
				) : items.length === 0 ? (
					<p className="text-sm text-zinc-500" data-testid="top-publishers-widget-empty">
						No publishers in this range.
					</p>
				) : (
					<div className="overflow-x-auto">
						<table
							className="w-full border-collapse"
							data-testid="top-publishers-widget-table"
						>
							<thead className="bg-zinc-50">
								<tr>
									<th className={TH}>Rank</th>
									<th className={TH}>Affiliate</th>
									<th className={`${TH} text-right`}>Clicks</th>
									<th className={`${TH} text-right`}>Conversions</th>
									<th className={`${TH} text-right`}>CR</th>
									<th className={`${TH} text-right`}>Payout</th>
									<th className={TH}>Trend</th>
								</tr>
							</thead>
							<tbody className="divide-y divide-zinc-200">
								{items.map((row, idx) => {
									const rank = idx + 1;
									return (
										<tr
											key={row.id}
											data-testid={`top-publishers-widget-row-${row.id}`}
										>
											<td className={`${TD} tabular-nums font-medium`}>{rank}</td>
											<td className={TD}>
												<div className="flex flex-col">
													<span className="font-medium text-zinc-900">
														{row.affiliate ?? row.id}
													</span>
													{row.email ? (
														<span className="text-xs text-zinc-500">
															{row.email}
														</span>
													) : null}
												</div>
											</td>
											<td className={`${TD} text-right tabular-nums`}>
												{formatNum(row.clicks)}
											</td>
											<td className={`${TD} text-right tabular-nums`}>
												{formatNum(row.conversions)}
											</td>
											<td className={`${TD} text-right tabular-nums`}>
												{formatPct(row.cr)}
											</td>
											<td className={`${TD} text-right tabular-nums font-medium text-emerald-700`}>
												{formatMoney(row.payout)}
											</td>
											<td className={TD}>
												<Sparkline points={row.sparkline ?? []} />
											</td>
										</tr>
									);
								})}
							</tbody>
						</table>
					</div>
				)}

				<div className="mt-5 flex justify-end border-t border-zinc-200 pt-4">
					<a
						href="/affiliates"
						className="text-sm font-medium text-sky-700 hover:text-sky-900 hover:underline"
						data-testid="top-publishers-widget-view-all"
					>
						View all publishers &rarr;
					</a>
				</div>
			</section>
		</main>
	);
}

export default TopPublishersWidgetPage;
